import { ErrorCode, type ApiEnvelope, type ApiError, type GetJobLambdaResultDto, type StripeCheckoutLambdaResult, type UploadLambdaResponseDto, type YoutubePreviewJob } from 'unvox-common';

type EmptyResponse = Record<string, never>;

function getApiBaseUrl(): string | null {
    const base = process.env.PUBLIC_API_BASE_URL;
    if (typeof base !== 'string' || base.trim() === '' || base === 'undefined') {
        return null;
    }

    // Normalize to avoid accidental double slashes when appending route paths.
    return base.replace(/\/+$/, '');
}

const API_BASE_URL = getApiBaseUrl();

export type JobStateResponse = ApiEnvelope<GetJobLambdaResultDto>;

function toErrorEnvelope<T>(code: string, message: string, details?: unknown): ApiEnvelope<T> {
    return {
        meta: {
            status: 'error',
            timestamp: new Date().toISOString(),
        },
        data: null,
        error: {
            code,
            message,
            ...(details !== undefined ? { details } : {}),
        },
    };
}

function toSuccessEnvelope<T>(data: T): ApiEnvelope<T> {
    return {
        meta: {
            status: 'ok',
            timestamp: new Date().toISOString(),
        },
        data,
        error: null,
    };
}

function hasApiEnvelopeShape(payload: unknown): payload is ApiEnvelope<unknown> {
    if (!payload || typeof payload !== 'object') return false;

    const candidate = payload as Partial<ApiEnvelope<unknown>>;
    if (!candidate.meta || typeof candidate.meta !== 'object') return false;

    const meta = candidate.meta as { status?: unknown; timestamp?: unknown };
    if (meta.status !== 'ok' && meta.status !== 'error') return false;
    if (typeof meta.timestamp !== 'string') return false;
    if (!('data' in candidate) || !('error' in candidate)) return false;

    if (candidate.error === null) return true;
    if (!candidate.error || typeof candidate.error !== 'object') return false;

    const error = candidate.error as Partial<ApiError>;
    return typeof error.code === 'string' && typeof error.message === 'string';
}

async function fetchApi<T>(path: string, init: RequestInit): Promise<ApiEnvelope<T>> {
    if (!API_BASE_URL) {
        return toErrorEnvelope(ErrorCode.MissingApiUrl, 'Missing PUBLIC_API_BASE_URL.');
    }

    const url = `${API_BASE_URL}${path}`;
    let response: Response;
    try {
        response = await fetch(url, init);
    } catch (error) {
        return toErrorEnvelope(ErrorCode.RequestFailed, 'Unable to reach the API.', error instanceof Error ? error.message : error);
    }

    if (!response.ok) {
        const details = {
            status: response.status,
            statusText: response.statusText,
        };
        return toErrorEnvelope(ErrorCode.RequestFailed, `API request failed with status ${response.status}.`, details);
    }

    let payload: unknown;
    try {
        payload = await response.json();
    } catch (error) {
        const details = {
            status: response.status,
            statusText: response.statusText,
            reason: error instanceof Error ? error.message : error,
        };
        return toErrorEnvelope(ErrorCode.UnexpectedResponse, 'Received a non-JSON response from the API.', details);
    }

    if (!hasApiEnvelopeShape(payload)) {
        const details = {
            status: response.status,
            statusText: response.statusText,
        };
        return toErrorEnvelope(ErrorCode.UnexpectedResponse, 'Received an unexpected API response shape.', details);
    }

    return payload as ApiEnvelope<T>;
}

export async function requestUpload(formData?: FormData): Promise<ApiEnvelope<UploadLambdaResponseDto>> {
    return fetchApi<UploadLambdaResponseDto>('/upload', {
        method: 'POST',
        ...(formData ? { body: formData } : {}),
    });
}

export async function uploadFileToPresignedPost(uploadUrl: string, fields: Record<string, string>, file: File): Promise<ApiEnvelope<EmptyResponse>> {
    const uploadTarget = uploadUrl.trim();
    if (!uploadTarget) {
        return toErrorEnvelope(ErrorCode.MissingUploadUrl, 'Missing upload URL.');
    }

    const s3FormData = new FormData();
    const resolvedFields = {
        ...fields,
        'x-amz-meta-original-filename': encodeURIComponent(file.name),
    };
    for (const [key, value] of Object.entries(resolvedFields)) {
        s3FormData.append(key, value);
    }
    s3FormData.append('file', file, file.name);

    let response: Response;
    try {
        response = await fetch(uploadTarget, {
            method: 'POST',
            body: s3FormData,
        });
    } catch (error) {
        return toErrorEnvelope(ErrorCode.UploadFailed, 'Unable to upload file.', error instanceof Error ? error.message : error);
    }

    if (!response.ok) {
        const details = {
            status: response.status,
            statusText: response.statusText,
        };
        return toErrorEnvelope(ErrorCode.UploadFailed, 'Unable to upload file.', details);
    }

    const emptyResponse: EmptyResponse = {};
    return toSuccessEnvelope<EmptyResponse>(emptyResponse);
}

export async function requestYoutubePreview(youtubeUrl: string): Promise<ApiEnvelope<YoutubePreviewJob>> {
    const trimmed = youtubeUrl.trim();
    if (!trimmed) {
        return toErrorEnvelope(ErrorCode.MissingYoutubeUrl, 'Missing youtubeUrl in request body.');
    }

    return fetchApi<YoutubePreviewJob>('/preview/youtube', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ youtubeUrl: trimmed }),
    });
}

interface GetJobStateOptions {
    sessionId?: string;
}

export async function getJobState(_jobId: string, options?: GetJobStateOptions): Promise<JobStateResponse> {
    const jobId = _jobId.trim();
    if (!jobId) {
        return toErrorEnvelope(ErrorCode.MissingJobId, 'Missing jobId in request path.');
    }

    const hasSessionId = options?.sessionId !== undefined;
    const sessionId = (options?.sessionId ?? '').trim();
    if (hasSessionId && !sessionId) {
        return toErrorEnvelope(ErrorCode.MissingSessionId, 'Missing session_id in request query.');
    }

    const query = hasSessionId ? `?${new URLSearchParams({ session_id: sessionId }).toString()}` : '';
    return fetchApi<GetJobLambdaResultDto>(`/job/${encodeURIComponent(jobId)}${query}`, {
        method: 'GET',
    });
}

interface RequestStripeCheckoutParams {
    jobId: string;
    email: string;
}

export async function requestStripeCheckout({ jobId, email }: RequestStripeCheckoutParams): Promise<ApiEnvelope<StripeCheckoutLambdaResult>> {
    const jobIdSanitized = jobId.trim();
    const emailSanitized = email.trim();

    if (!jobIdSanitized) {
        return toErrorEnvelope(ErrorCode.MissingJobId, 'Missing jobId in request body.');
    }

    if (!emailSanitized) {
        return toErrorEnvelope(ErrorCode.InvalidEmail, 'Invalid email address.');
    }

    return fetchApi<StripeCheckoutLambdaResult>('/checkout', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            jobId: jobIdSanitized,
            email: emailSanitized,
        }),
    });
}
